import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";
import {
  InboxOutlined,
  ReportProblem,
  VisibilityOutlined,
} from "@mui/icons-material";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { toast } from "react-toastify";
import {
  Avatar,
  IconButton,
  makeStyles,
  Tooltip,
} from "@material-ui/core";
import { format, isSameDay, parseISO } from "date-fns";
import { getBackendUrl } from "../../config";
import { brandAlpha, getTokens } from "../../styles/tokens";
import EmptyState from "../EmptyState";

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  const ellipsis = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  return {
    // um cartão-coluna por atendente; em telas estreitas vira uma coluna só
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(min(320px, 100%), 1fr))",
      gap: 16,
      fontFamily: tk.font,
    },
    column: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      height: 420,
      overflow: "hidden",
      backgroundColor: tk.paper,
      border: "1px solid " + tk.line,
      borderRadius: tk.radius.md,
      boxShadow: tk.shadow.card,
    },
    columnHead: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      gap: 12,
      minWidth: 0,
      padding: "12px 14px",
      backgroundColor: tk.muted,
      borderBottom: "1px solid " + tk.line,
    },
    // fila de espera: destaque discreto no tom de atenção
    columnHeadPending: {
      backgroundColor: tk.status.warn.bg,
    },
    headAvatar: {
      width: 36,
      height: 36,
      flex: "none",
    },
    headAvatarPending: {
      color: tk.status.warn.fg,
      backgroundColor: tk.paper,
      border: "1px solid " + tk.line,
    },
    headInfo: {
      flex: 1,
      minWidth: 0,
    },
    headName: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.35,
      color: tk.text,
      "& > span": ellipsis,
    },
    headCount: {
      marginTop: 1,
      fontSize: 12.5,
      color: tk.textSecondary,
      fontVariantNumeric: "tabular-nums",
    },
    pending: {
      flex: "none",
      color: tk.status.warn.fg,
      fontSize: 17,
    },
    list: {
      flex: 1,
      minHeight: 0,
      margin: 0,
      padding: 0,
      listStyle: "none",
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    item: {
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      padding: "12px 14px",
      borderBottom: "1px solid " + tk.lineRow,
      transition: "background-color 150ms ease",
      "&:last-child": { borderBottom: 0 },
      "&:hover": { backgroundColor: tk.rowHover },
    },
    itemAvatar: {
      width: 36,
      height: 36,
      flex: "none",
    },
    itemBody: {
      flex: 1,
      minWidth: 0,
    },
    itemTop: {
      display: "flex",
      alignItems: "baseline",
      gap: 8,
      minWidth: 0,
    },
    itemName: {
      flex: 1,
      minWidth: 0,
      fontSize: 13.5,
      fontWeight: 600,
      lineHeight: 1.35,
      color: tk.text,
      ...ellipsis,
    },
    lastMessageTime: {
      flex: "none",
      color: tk.textMuted,
      fontSize: 11,
      fontVariantNumeric: "tabular-nums",
    },
    lastMessageTimeUnread: {
      flex: "none",
      color: tk.brand,
      fontSize: 11,
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
    },
    itemMessage: {
      marginTop: 2,
      fontSize: 12.5,
      lineHeight: 1.4,
      color: tk.textSecondary,
      ...ellipsis,
    },
    itemTags: {
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
      marginTop: 6,
      minWidth: 0,
    },
    // selo neutro dos tokens; a fila só acrescenta o ponto e a tinta da cor dela
    tag: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      maxWidth: "100%",
      padding: "1px 7px",
      color: tk.status.neutral.fg,
      backgroundColor: tk.status.neutral.bg,
      borderRadius: tk.radius.badge,
      fontSize: 10.5,
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: "0.02em",
      "& > span:last-child": ellipsis,
    },
    tagDot: {
      width: 6,
      height: 6,
      borderRadius: "50%",
      flex: "none",
    },
    ticketLinkIcon: {
      flex: "none",
      padding: 6,
      marginRight: -6,
      color: tk.textMuted,
      "&:hover": { color: tk.brand },
    },
    emptyCard: {
      backgroundColor: tk.paper,
      border: "1px solid " + tk.line,
      borderRadius: tk.radius.md,
      boxShadow: tk.shadow.card,
    },
  };
});

const DashboardManage = () => {
  const classes = useStyles();
  const history = useHistory();
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const [tickets, setTickets] = useState([]);
  const [update, setUpdate] = useState(false);
  const [ticketNot, setTicketNot] = useState(0);
  const companyId = user.companyId;

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/usersMoments");
        console.log(data);
        setTickets(data);
        setUpdate(!update);
      } catch (err) {
        if (err.response?.status !== 500) {
          toastError(err);
        } else {
          toast.error(`${i18n.t("frontEndErrors.getUsers")}`);
        }
      }
    })();
  }, []);

  useEffect(() => {
    const companyId = user.companyId;
    console.log("socket painel");
    // const socket = socketManager.GetSocket();

    // const onConnect = () => {
    //   socket.emit("joinChatBox", `${ticketId}`);
    // }
    const onAppMessage = (data) => {
      if (
        data.action === "create" ||
        data.action === "update" ||
        data.action === "delete"
      ) {
        (async () => {
          try {
            const { data } = await api.get("/usersMoments");
            setTickets(data);
            setUpdate(!update);
          } catch (err) {
            if (err.response?.status !== 500) {
              toastError(err);
            } else {
              toast.error(`${i18n.t("frontEndErrors.getUsers")}`);
            }
          }
        })();
      }
    };

    // socket.on("connect", onConnect);
    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      // socket.off("connect", onConnect);
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket]);

  const renderTicket = (ticket, canOpen) => {
    const unread = Number(ticket.unreadMessages) > 0;
    return (
      <li className={classes.item} key={ticket.id}>
        <Avatar
          className={classes.itemAvatar}
          alt={ticket?.contact?.name || ""}
          src={ticket?.contact?.urlPicture || undefined}
        />
        <div className={classes.itemBody}>
          <div className={classes.itemTop}>
            <span className={classes.itemName}>{ticket?.contact?.name}</span>
            <span
              className={
                unread ? classes.lastMessageTimeUnread : classes.lastMessageTime
              }
            >
              {isSameDay(parseISO(ticket.updatedAt), new Date())
                ? format(parseISO(ticket.updatedAt), "HH:mm")
                : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
            </span>
          </div>
          <div className={classes.itemMessage}>{ticket.lastMessage || ""}</div>
          <div className={classes.itemTags}>
            {ticket?.whatsapp?.name && (
              <span className={classes.tag}>
                <span>{ticket.whatsapp.name}</span>
              </span>
            )}
            {ticket.queue?.name ? (
              <span
                className={classes.tag}
                style={{
                  backgroundColor: brandAlpha(
                    ticket.queue.color || "#7c7c7c",
                    0.12
                  ),
                }}
              >
                <span
                  className={classes.tagDot}
                  style={{ backgroundColor: ticket.queue.color || "#7c7c7c" }}
                />
                <span>{ticket.queue.name.toUpperCase()}</span>
              </span>
            ) : (
              <span className={classes.tag}>
                <span>{`${i18n.t("momentsUser.noqueue")}`}</span>
              </span>
            )}
          </div>
        </div>
        {canOpen && (
          <Tooltip title="Acessar Ticket">
            <IconButton
              size="small"
              className={classes.ticketLinkIcon}
              aria-label="Acessar Ticket"
              onClick={() => history.push(`/tickets/${ticket.uuid}`)}
            >
              <VisibilityOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </li>
    );
  };

  const Moments = useMemo(() => {
    // console.log(tickets)
    if (tickets && tickets.length > 0) {
      const ticketsByUser = tickets.reduce((userTickets, ticket) => {
        const user = ticket.user;

        if (user) {
          const userIndex = userTickets.findIndex(
            (group) => group.user.id === user.id
          );
          if (userIndex === -1) {
            userTickets.push({
              user,
              userTickets: [ticket],
            });
          } else {
            userTickets[userIndex].userTickets.push(ticket);
          }
        }
        return userTickets;
      }, []);

      return ticketsByUser.map((group, index) => (
        <section className={classes.column} key={index}>
          <header className={classes.columnHead}>
            <Avatar
              className={classes.headAvatar}
              alt={group?.user?.name || ""}
              src={
                group.user.profileImage
                  ? `${backendUrl}/public/company${companyId}/user/${group.user.profileImage}`
                  : null
              }
            />
            <div className={classes.headInfo}>
              <div className={classes.headName}>
                <span>
                  {group?.user?.name || `${i18n.t("momentsUser.pending")}`}
                </span>
              </div>
              <div className={classes.headCount}>
                {`${i18n.t("momentsUser.services")} ${
                  group.userTickets?.length
                }`}
              </div>
            </div>
          </header>
          <ul className={classes.list}>
            {group.userTickets.map((ticket) =>
              renderTicket(
                ticket,
                user.profile === "admin" || ticket.userId === user.id
              )
            )}
          </ul>
        </section>
      ));
    } else {
      return null;
    }
    // `classes` entra para os cartões acompanharem a troca de tema (claro/escuro)
  }, [update, classes]);

  const MomentsPending = useMemo(() => {
    if (tickets && tickets.length > 0) {
      const pendingTickets = tickets.filter((ticket) => !ticket.user);

      return (
        <section className={classes.column}>
          <header
            className={`${classes.columnHead} ${classes.columnHeadPending}`}
          >
            <Avatar
              className={`${classes.headAvatar} ${classes.headAvatarPending}`}
            />
            <div className={classes.headInfo}>
              <div className={classes.headName}>
                <span>{`${i18n.t("momentsUser.pending")}`}</span>
                <ReportProblem className={classes.pending} />
              </div>
              <div className={classes.headCount}>
                {i18n.t("momentsUser.services")} {pendingTickets?.length}
              </div>
            </div>
          </header>
          <ul className={classes.list}>
            {pendingTickets.map((ticket) => renderTicket(ticket, false))}
          </ul>
        </section>
      );
    } else {
      return null;
    }
  }, [update, classes]);

  if (!tickets || tickets.length === 0) {
    return (
      <div className={classes.emptyCard}>
        <EmptyState
          icon={<InboxOutlined />}
          title="Nenhum atendimento ativo"
          message="Assim que houver conversas em andamento, a distribuição por atendente aparecerá aqui."
        />
      </div>
    );
  }

  return (
    <div className={classes.grid}>
      {Moments}
      {MomentsPending}
    </div>
  );
};

export default DashboardManage;
